import { STRIPE_FREE_FOR_LIFE_PLAN_KEY } from '@nfp-tracker/utils';

export interface SubscriptionStatusComputationInput {
  subscriptionStatus: string | null;
  trialEndDate: string | null;
  subscriptionEndDate: string | null;
  planKey: string | null;
  now: Date;
}

export interface SubscriptionStatusComputationResult {
  hasAccess: boolean;
  status: string;
  daysRemaining: number | null;
  isGracePeriod: boolean;
  freeForLife: boolean;
}

const DAY_IN_MILLISECONDS = 1000 * 60 * 60 * 24;
const GRACE_PERIOD_DAYS = 3;

export interface SubscriptionDateMessaging {
  label: 'Trial ends' | 'Next renewal' | 'Access ends';
  date: string;
}

export const deriveSubscriptionDateMessaging = ({
  subscriptionStatus,
  trialEndDate,
  subscriptionEndDate,
  subscriptionRenewalDate,
  subscriptionAutoRenews,
  freeForLife,
}: {
  subscriptionStatus: string | null;
  trialEndDate: string | null;
  subscriptionEndDate: string | null;
  subscriptionRenewalDate: string | null;
  subscriptionAutoRenews: boolean | null;
  freeForLife: boolean;
}): SubscriptionDateMessaging | null => {
  if (freeForLife) return null;

  const status = subscriptionStatus ?? '';

  if (status === 'trialing' && trialEndDate) {
    return { label: 'Trial ends', date: trialEndDate };
  }

  if ((status === 'active' || status === 'past_due') && subscriptionAutoRenews === false && subscriptionEndDate) {
    return { label: 'Access ends', date: subscriptionEndDate };
  }

  if ((status === 'active' || status === 'past_due') && subscriptionRenewalDate) {
    return { label: 'Next renewal', date: subscriptionRenewalDate };
  }

  if ((status === 'active' || status === 'past_due') && subscriptionAutoRenews === true && subscriptionEndDate) {
    return { label: 'Next renewal', date: subscriptionEndDate };
  }

  if ((status === 'active' || status === 'past_due') && subscriptionEndDate) {
    return { label: 'Access ends', date: subscriptionEndDate };
  }

  return null;
};

export const computeSubscriptionStatus = ({
  subscriptionStatus,
  trialEndDate,
  subscriptionEndDate,
  planKey,
  now,
}: SubscriptionStatusComputationInput): SubscriptionStatusComputationResult => {
  const freeForLife = planKey === STRIPE_FREE_FOR_LIFE_PLAN_KEY;
  const status = freeForLife ? 'active' : (subscriptionStatus || 'trialing');
  const trialEnd = trialEndDate ? new Date(trialEndDate) : null;
  const subEnd = subscriptionEndDate ? new Date(subscriptionEndDate) : null;

  let hasAccess = false;
  let daysRemaining: number | null = null;
  let isGracePeriod = false;

  if (freeForLife) {
    hasAccess = true;
  } else if (status === 'trialing' && trialEnd) {
    hasAccess = trialEnd > now;
    if (hasAccess) {
      daysRemaining = Math.ceil((trialEnd.getTime() - now.getTime()) / DAY_IN_MILLISECONDS);
    }
  } else if (status === 'active') {
    hasAccess = true;
  } else if (status === 'past_due' && subEnd) {
    const gracePeriodEnd = new Date(subEnd.getTime() + GRACE_PERIOD_DAYS * DAY_IN_MILLISECONDS);
    hasAccess = gracePeriodEnd > now;
    isGracePeriod = hasAccess;
    if (hasAccess) {
      daysRemaining = Math.ceil((gracePeriodEnd.getTime() - now.getTime()) / DAY_IN_MILLISECONDS);
    }
  }

  return {
    hasAccess,
    status,
    daysRemaining,
    isGracePeriod,
    freeForLife,
  };
};
